import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";
import BrandText from "@/components/BrandText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Canonical category identifiers persisted server-side (English) —
// display labels swap by locale so Arabic users read Arabic while the
// backend record remains stable.
const CATEGORY_IDS = ["Apparel", "Riding Gear", "Emblems & Accessories", "Limited Editions"];
const CATEGORY_LABELS = {
    en: {
        "Apparel": "Apparel",
        "Riding Gear": "Riding Gear",
        "Emblems & Accessories": "Emblems & Accessories",
        "Limited Editions": "Limited Editions",
    },
    ar: {
        "Apparel": "الملابس",
        "Riding Gear": "معدات القيادة",
        "Emblems & Accessories": "الشعارات والإكسسوارات",
        "Limited Editions": "الإصدارات المحدودة",
    },
};
const SIZE_IDS = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "Not sure yet"];
const SIZE_LABELS = {
    en: { "Not sure yet": "Not sure yet" },
    ar: { "Not sure yet": "لم يتم التحديد" },
};
const QUANTITIES = ["1", "2", "3", "4", "5", "6+"];

function CollectionInterestSection({ isAr }) {
    const [form, setForm] = useState({
        full_name: "", email: "", mobile: "",
        categories: [], sizes: [], quantity: "1",
        comments: "", acknowledged: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const toggle = (key, value) => {
        setForm((f) => {
            const set = new Set(f[key]);
            set.has(value) ? set.delete(value) : set.add(value);
            return { ...f, [key]: Array.from(set) };
        });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.acknowledged) {
            setError(isAr ? "يرجى الإقرار للمتابعة." : "Please acknowledge the notice to continue.");
            return;
        }
        if (form.categories.length === 0) {
            setError(isAr ? "يرجى اختيار فئة واحدة على الأقل." : "Please select at least one category.");
            return;
        }
        if (form.sizes.length === 0) {
            setError(isAr ? "يرجى اختيار مقاس واحد على الأقل." : "Please select at least one size.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API_BASE}/collection-interest`, form);
            setSubmitted(true);
        } catch (err) {
            setError(isAr ? "حدث خطأ. يرجى المحاولة مجددًا." : "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const catLabel = (id) => (isAr ? CATEGORY_LABELS.ar[id] : CATEGORY_LABELS.en[id]);
    const sizeLabel = (id) => (isAr && SIZE_LABELS.ar[id]) ? SIZE_LABELS.ar[id] : id;

    if (submitted) {
        return (
            <section className="px-6 md:px-12 lg:px-20 py-24 max-w-4xl mx-auto text-center" data-testid="collection-interest-success">
                <p className="overline mb-6">
                    {isAr ? <>مجموعة <Brand /> الرسمية</> : <>The <Brand /> Collection</>}
                </p>
                <h2 className="font-display text-4xl md:text-5xl text-[var(--crewz-text)] leading-[0.98]">
                    {isAr ? "شكرًا لك." : "Thank you."}
                </h2>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/80 mt-6 font-light leading-relaxed max-w-2xl mx-auto">
                    {isAr ? (
                        <>شكرًا لمساهمتك في تشكيل أول مجموعة من <Brand />. تم استلام مشاركتك. وعند توفر المنتجات التي اخترتها، سنُعلمك عبر البريد الإلكتروني.</>
                    ) : (
                        <>Thank you for helping shape the first <Brand /> Collection. Your submission has been received. If the products you&apos;ve selected become available, we&apos;ll let you know by email.</>
                    )}
                </p>
            </section>
        );
    }

    return (
        <section className="px-6 md:px-12 lg:px-20 py-24 max-w-4xl mx-auto" data-testid="collection-interest">
            <div className="flex items-center gap-3 mb-5">
                <span className="gold-rule" />
                <p className="overline">
                    {isAr ? <>مجموعة <Brand /> الرسمية</> : <>The <Brand /> Collection</>}
                </p>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--crewz-text)] leading-[0.98] max-w-3xl">
                {isAr ? "ساهم في تشكيل أول مجموعة" : "Help Shape the First Collection"}
            </h2>
            <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/80 mt-6 max-w-3xl font-light leading-relaxed">
                {isAr ? (
                    <>يجري حاليًا تطوير أول مجموعة من <Brand />. وقبل بدء الإنتاج، نود التعرف على المنتجات التي تهمكم، والمقاسات التي تفضلونها، والكميات التي قد ترغبون في اقتنائها. وستساعدنا آراؤكم في إنتاج المجموعة المناسبة، بالكميات المناسبة، للمجتمع. ولا يمثل إرسال هذا النموذج طلب شراء، أو حجزًا لأي منتج، أو أي التزام بالدفع.</>
                ) : (
                    <>The first <Brand /> Collection is currently in development. Before production begins, we&apos;d like to understand which products interest you, your preferred size and the quantity you may wish to purchase. Your response helps <Brand /> produce the right collection, in the right quantities, for the community. Submitting this form does not place an order, reserve any product or require payment.</>
                )}
            </p>

            <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="collection-interest-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="overline block mb-2">{isAr ? "الاسم الكامل" : "Full name"}</label>
                        <input required type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ci-name" className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[var(--crewz-gold)] outline-none font-body" />
                    </div>
                    <div>
                        <label className="overline block mb-2">{isAr ? "البريد الإلكتروني" : "Email"}</label>
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ci-email" className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[var(--crewz-gold)] outline-none font-body" dir="ltr" />
                    </div>
                    <div>
                        <label className="overline block mb-2">{isAr ? "رقم الجوال" : "Mobile"}</label>
                        <input required type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="ci-mobile" className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[var(--crewz-gold)] outline-none font-body" dir="ltr" />
                    </div>
                    <div>
                        <label className="overline block mb-2">{isAr ? "الكمية التقريبية" : "Approximate quantity"}</label>
                        <select value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="ci-quantity" className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[var(--crewz-gold)] outline-none font-body">
                            {QUANTITIES.map((q) => <option key={q} value={q} className="bg-[var(--crewz-bg)]">{q}</option>)}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="overline block mb-3">{isAr ? "الفئات المفضلة" : "Preferred categories"}</label>
                    <div className="flex flex-wrap gap-3">
                        {CATEGORY_IDS.map((c) => (
                            <button type="button" key={c} onClick={() => toggle("categories", c)} data-testid={`ci-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                                className={`px-4 py-2 text-sm tracking-[0.12em] ${isAr ? "" : "uppercase"} border transition-colors ${form.categories.includes(c) ? "border-[var(--crewz-gold)] text-[var(--crewz-gold)]" : "border-white/20 text-[var(--crewz-text)]/80 hover:border-white/40"}`}>
                                {catLabel(c)}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="overline block mb-3">{isAr ? "المقاسات المفضلة" : "Preferred sizes"}</label>
                    <div className="flex flex-wrap gap-3">
                        {SIZE_IDS.map((s) => (
                            <button type="button" key={s} onClick={() => toggle("sizes", s)} data-testid={`ci-size-${s.toLowerCase().replace(/\s/g, "-")}`}
                                className={`px-4 py-2 text-sm tracking-[0.12em] ${isAr && SIZE_LABELS.ar[s] ? "" : "uppercase"} border transition-colors ${form.sizes.includes(s) ? "border-[var(--crewz-gold)] text-[var(--crewz-gold)]" : "border-white/20 text-[var(--crewz-text)]/80 hover:border-white/40"}`}>
                                {sizeLabel(s)}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="overline block mb-2">{isAr ? "ملاحظات أو تفضيلات (اختياري)" : "Comments or preferences (optional)"}</label>
                    <textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="ci-comments" rows={3} className="w-full bg-transparent border-b border-white/20 py-3 focus:border-[var(--crewz-gold)] outline-none font-body resize-none" />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.acknowledged} onChange={(e) => setForm({ ...form, acknowledged: e.target.checked })} data-testid="ci-acknowledge" className="mt-1 accent-[var(--crewz-gold)]" />
                    <span className="font-body text-sm text-[var(--crewz-text)]/75 font-light leading-relaxed">
                        {isAr ? (
                            <>أقر بأن هذا النموذج مخصص لإبداء الاهتمام فقط، ولا يمثل طلب شراء، أو حجزًا لأي منتج، وأن بياناتي ستُستخدم حصريًا لمساعدة <Brand /> في التخطيط للإنتاج المستقبلي.</>
                        ) : (
                            <>I understand this is an expression of interest only. It does not constitute an order, does not reserve any product, and my details will be used solely to help <Brand /> plan future production.</>
                        )}
                    </span>
                </label>

                {error && <p className="text-sm text-red-400" data-testid="ci-error">{error}</p>}

                <button type="submit" disabled={submitting} data-testid="ci-submit"
                    className={`px-8 py-4 border border-[var(--crewz-gold)] text-[var(--crewz-gold)] text-sm tracking-[0.22em] ${isAr ? "" : "uppercase"} font-bold hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] transition-colors disabled:opacity-50`}>
                    {submitting
                        ? (isAr ? "جاري الإرسال…" : "Submitting...")
                        : (isAr ? "شاركنا اهتمامك" : "Share Your Interest")}
                </button>
            </form>
        </section>
    );
}

// Store page — APPROVED bilingual (Feb 2026). Structure/imagery/colours
// preserved between /store and /ar/store; only editorial strings and
// form labels swap by locale. Backend contract (canonical English
// identifiers for categories and sizes) unchanged.

const STORE_IMAGES = Array.from({ length: 6 }, (_, i) => ({
    src: `/assets/store/store-${String(i + 1).padStart(2, "0")}.jpg`,
    alt: `CrewZNatioN store preview ${i + 1}`,
}));

const COLLECTION_CATEGORIES_EN = [
    { name: "Apparel", note: "Tees, hoodies, outerwear, and headwear." },
    { name: "Riding Gear", note: "Helmets and protective wear for the road." },
    { name: "Emblems & Accessories", note: "Stickers, patches, keychains, license plate frames, and small everyday pieces." },
    { name: "Limited Editions", note: "Collectibles, capsules, and collaborations produced alongside key CrewZNatioN moments." },
];

const COLLECTION_CATEGORIES_AR = [
    { name: "الملابس", note: "قمصان، وهوديات، وملابس خارجية، وأغطية للرأس." },
    { name: "معدات القيادة", note: "خوذ، ومعدات حماية مخصصة للقيادة على الطرق." },
    { name: "الشعارات والإكسسوارات", note: "ملصقات، ورقع، وسلاسل مفاتيح، وإطارات للوحات المركبات، وإكسسوارات يومية صغيرة." },
    { name: "الإصدارات المحدودة", note: "قطع مقتناة، ومجموعات حصرية، وتعاونات خاصة تُطرح بالتزامن مع أبرز محطات CrewZNatioN." },
];

function Lightbox({ images, index, onClose, onPrev, onNext, isAr }) {
    useEffect(() => {
        if (index === null) return undefined;
        const handler = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowLeft") onPrev();
            if (e.key === "ArrowRight") onNext();
        };
        window.addEventListener("keydown", handler);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", handler);
            document.body.style.overflow = previousOverflow;
        };
    }, [index, onClose, onPrev, onNext]);

    if (index === null) return null;
    const img = images[index];

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center"
            onClick={onClose}
            data-testid="store-lightbox"
        >
            <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white p-2 text-3xl z-10" aria-label={isAr ? "إغلاق" : "Close"}>×</button>
            <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-[var(--crewz-gold)] p-2 text-4xl z-10" aria-label={isAr ? "السابق" : "Previous"}>‹</button>
            <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/60 hover:text-[var(--crewz-gold)] p-2 text-4xl z-10" aria-label={isAr ? "التالي" : "Next"}>›</button>
            <div className="max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
                <img src={img.src} alt={img.alt} className="max-w-full max-h-[85vh] object-contain" />
                <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 font-medium text-center mt-4" dir="ltr">
                    {index + 1} / {images.length}
                </p>
            </div>
        </div>
    );
}

export default function Store({ locale = "en" }) {
    const isAr = locale === "ar";
    const [active, setActive] = useState(null);

    const open = (i) => setActive(i);
    const close = () => setActive(null);
    const prev = () => setActive((i) => (i - 1 + STORE_IMAGES.length) % STORE_IMAGES.length);
    const next = () => setActive((i) => (i + 1) % STORE_IMAGES.length);

    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;
    const CATEGORY_CARDS = isAr ? COLLECTION_CATEGORIES_AR : COLLECTION_CATEGORIES_EN;

    return (
        <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-[var(--crewz-bg)]" data-testid="store-page">
            <Nav />

            {/* HERO */}
            <section className="px-6 md:px-12 lg:px-20 pt-20 pb-12 max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-5">
                    <span className="gold-rule" />
                    <p className="overline">
                        {isAr ? <>مجموعة <Brand /> الرسمية</> : <>The <Brand /> Collection</>}
                    </p>
                </div>
                <h1
                    className={`font-display text-5xl md:text-6xl text-[var(--crewz-text)] leading-[0.98] max-w-3xl${isAr ? " !leading-[60px] md:!leading-[72px]" : ""}`}
                    data-testid="store-title"
                >
                    {isAr ? "الثقافة… تُرتدى." : "Culture, worn well."}
                </h1>
                <p className="font-body text-lg md:text-xl text-[var(--crewz-text)]/85 mt-8 max-w-3xl font-light leading-relaxed">
                    {isAr ? (
                        <>يجري حاليًا تطوير أول مجموعة من <Brand />. وتُصمم كل قطعة وفق المعايير ذاتها، وبنفس مستوى العناية الذي يميز <Brand /> كمؤسسة. وسيتم إطلاق المجموعة عند اكتمال جاهزيتها.</>
                    ) : (
                        <>The first <Brand /> Collection is in development. Every piece is created with the same care and standards that define the institution. The Collection will be released when it is ready.</>
                    )}
                </p>
            </section>

            {/* PRODUCT PREVIEW GRID */}
            <section
                className="px-6 md:px-12 lg:px-20 pt-8 pb-20 max-w-7xl mx-auto"
                data-testid="store-preview-grid"
            >
                <div className="flex items-center gap-3 mb-8">
                    <span className="gold-rule" />
                    <p className="overline">{isAr ? "نظرة أولى" : "First Look"}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {STORE_IMAGES.map((item, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => open(i)}
                            className="group relative aspect-square overflow-hidden bg-[var(--crewz-surface)] text-left"
                            data-testid={`store-tile-${i + 1}`}
                        >
                            <img
                                src={item.src}
                                alt={item.alt}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--crewz-gold-soft)] font-medium">
                                    {isAr ? "معاينة" : "Preview"}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* COMING SOON CATEGORIES */}
            <section className="section-light" data-testid="store-categories">
                <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32 max-w-6xl mx-auto">
                    <div className="flex items-center gap-3 mb-5">
                        <span className="gold-rule" />
                        <p
                            className="overline"
                            style={{ color: "var(--crewz-text-dark)", opacity: 0.7 }}
                        >
                            {isAr ? "مجموعة المنتجات" : "The Collection"}
                        </p>
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl text-[var(--crewz-text-dark)] leading-[0.98]">
                        {isAr ? "ماذا ستتضمن المجموعة؟" : "What the collection will include."}
                    </h2>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {CATEGORY_CARDS.map((c) => (
                            <div
                                key={c.name}
                                className="border border-[var(--crewz-text-dark)]/10 bg-white/50 p-8 hover:border-[var(--crewz-gold)]/60 transition-colors duration-300"
                                data-testid={`store-category-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                            >
                                <h3 className="font-display text-3xl text-[var(--crewz-text-dark)]">
                                    {c.name}
                                </h3>
                                <p className="font-body text-sm text-[var(--crewz-text-dark)]/70 mt-3 leading-relaxed font-light">
                                    <BrandText>{c.note}</BrandText>
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THE CREWZNATION COLLECTION — INTEREST REGISTRATION */}
            <CollectionInterestSection isAr={isAr} />

            {/* CTA — Membership */}
            <section className="px-6 md:px-12 lg:px-20 py-24 max-w-4xl mx-auto text-center">
                <p className="overline mb-6">{isAr ? "العضوية" : "Membership"}</p>
                <h3 className={`font-display text-4xl md:text-5xl text-[var(--crewz-text)] leading-[0.98]${isAr ? " !leading-[43.2px] md:!leading-[57.6px]" : ""}`}>
                    {isAr ? "الأعضاء هم أول من يحصل على المجموعة." : "The collection reaches members first."}
                </h3>
                <p className="font-body text-base md:text-lg text-[var(--crewz-text)]/80 mt-6 font-light leading-relaxed">
                    {isAr ? "عند توفر المجموعة، يتم إشعار الأعضاء أولًا." : "When the collection becomes available, members are notified first."}
                </p>
                <a
                    href={isAr ? "/ar/contact" : "/contact"}
                    className={`inline-flex items-center mt-10 text-sm tracking-[0.22em] ${isAr ? "" : "uppercase"} font-bold border border-[var(--crewz-gold)] text-[var(--crewz-gold)] hover:bg-[var(--crewz-gold)] hover:text-[var(--crewz-bg)] px-7 py-4 transition-all duration-300`}
                    data-testid="store-cta-membership"
                >
                    {isAr ? "قدّم طلب العضوية" : "Apply for Membership"}
                </a>
            </section>

            <Lightbox
                images={STORE_IMAGES}
                index={active}
                onClose={close}
                onPrev={prev}
                onNext={next}
                isAr={isAr}
            />

            <PageFooter />
        </div>
    );
}
